import { useState, useEffect, useCallback } from "react";
import api from "../utils/api/axiosInstance";
import { useFavoritesContext } from "../contexts/FavoritesContext";

/**
 * Shared favorite state + toggle for a single product.
 *
 * When a FavoritesProvider is mounted (the normal app case), this reads from
 * the shared favorites list — loaded once via GET /users/me/favorites —
 * instead of every card independently calling
 * GET /users/me/favorites/:productId (previously N+1 requests per grid).
 * Falls back to the old per-product fetch if no provider is present.
 */
export default function useProductFavorite(productId, { enabled = true } = {}) {
  const favoritesContext = useFavoritesContext();
  const [toggling, setToggling] = useState(false);

  // --- Fallback path (no FavoritesProvider in tree) ---
  const [localIsFavorite, setLocalIsFavorite] = useState(false);

  useEffect(() => {
    if (favoritesContext) return undefined;
    if (!productId || !enabled) {
      setLocalIsFavorite(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get(`/users/me/favorites/${productId}`);
        if (!cancelled) {
          setLocalIsFavorite(!!data?.isFavorite);
        }
      } catch (err) {
        if (
          !cancelled &&
          err?.response?.status &&
          err.response.status !== 401
        ) {
          console.error(
            "Failed to check favorite status:",
            err.response?.data || err.message,
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [favoritesContext, productId, enabled]);

  const isFavorite = favoritesContext
    ? favoritesContext.isFavorite(productId)
    : localIsFavorite;

  const setIsFavorite = useCallback(
    (value) => {
      if (favoritesContext) {
        favoritesContext.setFavorite(productId, value);
      } else {
        setLocalIsFavorite(value);
      }
    },
    [favoritesContext, productId],
  );

  const toggleFavorite = useCallback(async () => {
    if (!productId) {
      return { success: false };
    }

    if (favoritesContext) {
      setToggling(true);
      try {
        const result = await favoritesContext.toggleFavorite(productId);
        if (!result.success) {
          console.error(
            "Favorite update failed:",
            result.error?.response?.data || result.error?.message,
          );
        }
        return result;
      } finally {
        setToggling(false);
      }
    }

    try {
      setToggling(true);
      const nextIsFavorite = !localIsFavorite;

      if (localIsFavorite) {
        await api.delete("/users/me/favorites", {
          data: { productId },
        });
      } else {
        await api.post("/users/me/favorites", { productId });
      }

      setLocalIsFavorite(nextIsFavorite);
      return { success: true, isFavorite: nextIsFavorite };
    } catch (err) {
      console.error(
        "Favorite update failed:",
        err.response?.data || err.message,
      );
      return { success: false, error: err };
    } finally {
      setToggling(false);
    }
  }, [productId, favoritesContext, localIsFavorite]);

  return { isFavorite, setIsFavorite, toggleFavorite, toggling };
}
